/* eslint-disable eqeqeq */
/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import CustomLayout from "../../../../Core/Layout/CustomLayout";
import Button from "../../../../Core/Components/CustomButton";
import { PopoverDropdown } from "../../../../Core/Components/CustomDropdown";
import {
  BATTERY_REQUEST_TRANSFER_TYPE,
  BATTERY_REQUEST_TYPE,
  BATTERY_STATUS,
  batteryRequestTypeOptions,
  batteryTransferForTripOptions,
} from "../../Data";
import { CustomInputWithController } from "../../../../Core/Components/CustomInput";
import { updateBatteries } from "../../../../Redux/Slices/batteries.slice";
import { client } from "../../../../Utils/axiosClient";
import SelectSwapBatteries from "./SelectSwapBatteries";
import useReceiverHubStations from "./useReceiverHubStations";
import { getRentalIds, getTripBatteries, getTripIds } from "./api";
import useSupplierHubStations from "./useSupplierHubStations";

const rules = { required: "Required." };

const RequestBatteries = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { allBatteries } = useSelector((state) => state.batteries);

  const {
    handleSubmit,
    control,
    formState,
    setValue,
    clearErrors,
    watch,
    setError,
  } = useForm({
    defaultValues: {
      receiverHubId: null,
      receiverHubName: "",
      supplierHubId: null,
      supplierHubName: "",
      requestTypeId: null,
      requestType: "",
      tripTransferId: null,
      tripTransfer: "",
      tripId: null,
      batteryCount: null,
    },
  });

  const recevierHubStations = useReceiverHubStations();

  const supplierHubStations = useSupplierHubStations({
    receiverHubId: watch("receiverHubId"),
  });

  const [inputFocus, setInputFocus] = useState(false);
  const [tripIds, setTripIds] = useState([]);
  const [rentalIds, setRentalIds] = useState([]);
  const [tripBatteryIds, setTripBatteryIds] = useState([]);
  const [selectedBatteryIds, setSelectedBatteryIds] = useState([]);
  const [selectedBatteryError, setSelectedBatteryError] = useState("");

  const receiverHubNameError = formState?.errors?.receiverHubName?.message;
  const supplierHubNameError = formState?.errors?.supplierHubName?.message;
  const requestTypeError = formState?.errors?.requestType?.message;
  const tripTransferError = formState?.errors?.tripTransfer?.message;
  const tripIdError = formState?.errors?.tripId?.message;

  // watch the trip id
  const localTripId = watch("tripId");

  const getBatteryCount = () => {
    let batteryCount = 0;
    const hubId = watch("supplierHubId");

    if (hubId) {
      batteryCount = allBatteries.filter(
        (item) =>
          +item.currentHubId === hubId &&
          item.status === BATTERY_STATUS.HUB_STATION,
      ).length;
    }

    return batteryCount;
  };

  const onTripBatterySelect = (batteryId) => {
    setSelectedBatteryError("");

    setSelectedBatteryIds((prev) => {
      if (prev.includes(batteryId)) {
        return prev.filter((item) => item !== batteryId);
      }
      return [...prev, batteryId];
    });
  };

  const onCancel = () => {
    navigate("/batteries");
  };

  const onSubmit = async (e) => {
    // error if no batteries are selected for the swap
    // NOTE -> this error should not be checked for rental request
    if (
      e.requestTypeId !== BATTERY_REQUEST_TYPE.RENTAL &&
      e.tripTransferId === BATTERY_REQUEST_TRANSFER_TYPE.SWAP &&
      selectedBatteryIds.length === 0
    ) {
      setSelectedBatteryError("Please select the batteries for swap.");
      return;
    }

    // error if requested count is greater than the batteries available at the supplier hub station
    // NOTE -> this error should not be checked for rental request
    if (
      e.requestTypeId !== BATTERY_REQUEST_TYPE.RENTAL &&
      e.batteryCount > getBatteryCount()
    ) {
      setError("batteryCount", {
        type: "server",
        message: "Not enough batteries available",
      });

      return;
    }

    const payload = e;

    if (e.tripTransferId === BATTERY_REQUEST_TRANSFER_TYPE.SWAP) {
      payload.swapBatteryIds = selectedBatteryIds;
    }

    try {
      dispatch(updateBatteries({ key: "loading", value: true }));

      await client.post(
        `${process.env.REACT_APP_API_URL}/battery/request-batteries`,
        payload,
      );

      const description =
        e.requestTypeId === BATTERY_REQUEST_TYPE.RENTAL
          ? "Your request for batteries was sent successfully."
          : `Your request of ${e.batteryCount} batteries was sent successfully.`;

      dispatch(
        updateBatteries({
          key: "showToastMessage",
          value: {
            visible: true,
            data: {
              title: "Request Sent!",
              description,
            },
          },
        }),
      );

      navigate("/batteries");
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(updateBatteries({ key: "loading", value: false }));
    }
  };

  // get trip and rental ids
  useEffect(() => {
    getTripIds({ setTripIds });
    getRentalIds({ setRentalIds });
  }, []);

  // get trip batteries only when trip id is selected
  useEffect(() => {
    if (watch("tripId")) getTripBatteries({ localTripId, setTripBatteryIds });
  }, [watch, localTripId]);

  return (
    <CustomLayout pageName="Batteries" hideFooter>
      <div className="px-6 pt-6 bg-background-white">
        <div className="flex items-center justify-between pb-6">
          <p className="font-poppins text-base font-medium capitalize">
            Request Batteries
          </p>

          <div className="flex items-center gap-x-3">
            <Button
              size="filter"
              width="generateUser"
              colors="grey"
              onClick={onCancel}
            >
              Cancel
            </Button>

            <Button
              size="filter"
              width="generateUser"
              colors="primary"
              onClick={handleSubmit(onSubmit)}
            >
              Send Request
            </Button>
          </div>
        </div>

        <div className="w-full bg-light-grey h-[1px]" />
      </div>

      <div className="h-[calc(100vh-72px-89px)] p-6 bg-background-white overflow-y-scroll">
        <div className="">
          <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
            Receiver Hub Station
          </p>

          <Controller
            name="receiverHubName"
            control={control}
            rules={rules}
            render={({ field }) => (
              <PopoverDropdown
                {...field}
                enableSearch
                searchPlaceholder="Search Hub Station"
                placeholder="Assign Hub Station"
                className="max-h-[300px] overflow-y-scroll"
                formState={formState}
                options={recevierHubStations}
                onSelect={(data) => {
                  setValue("receiverHubName", data.value);
                  setValue("receiverHubId", data.id);
                  setValue("supplierHubName", "");
                  setValue("supplierHubId", null);
                  clearErrors("receiverHubName");
                }}
              />
            )}
          />

          {receiverHubNameError && (
            <p
              className={`font-poppins text-xs font-light capitalize leading-4.5 ${
                receiverHubNameError
                  ? "text-alert-red visible mt-2"
                  : "invisible"
              }`}
            >
              {receiverHubNameError}
            </p>
          )}
        </div>

        <div className="mt-6">
          <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
            Supplier Hub Station
          </p>

          <Controller
            name="supplierHubName"
            control={control}
            rules={rules}
            render={({ field }) => (
              <PopoverDropdown
                {...field}
                enableSearch
                searchPlaceholder="Search Hub Station"
                placeholder="Assign Hub Station"
                className="max-h-[300px] overflow-y-scroll"
                formState={formState}
                options={supplierHubStations}
                onSelect={(data) => {
                  setValue("supplierHubName", data.value);
                  setValue("supplierHubId", data.id);
                  clearErrors("supplierHubName");
                }}
              />
            )}
          />

          {supplierHubNameError ? (
            <p
              className={`font-poppins text-xs font-light capitalize leading-4.5 ${
                supplierHubNameError
                  ? "text-alert-red visible mt-2"
                  : "invisible"
              }`}
            >
              {supplierHubNameError}
            </p>
          ) : (
            <div className="mt-3 flex items-center justify-between w-[367px] pr-1 font-poppins text-xs font-medium">
              <p>Available Batteries</p>
              <p>{getBatteryCount()}</p>
            </div>
          )}
        </div>

        <div className="w-[367px] h-[1px] my-6 bg-light-grey" />

        <div className="">
          <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
            Request Type
          </p>

          <Controller
            name="requestType"
            control={control}
            rules={rules}
            render={({ field }) => (
              <PopoverDropdown
                {...field}
                placeholder="Select request type"
                formState={formState}
                options={batteryRequestTypeOptions}
                onSelect={(data) => {
                  setValue("requestType", data.value);
                  setValue("requestTypeId", data.id);

                  if (data.id === BATTERY_REQUEST_TYPE.HUB) {
                    setValue(
                      "tripTransferId",
                      BATTERY_REQUEST_TRANSFER_TYPE.TRANSFER,
                    );
                  }

                  if (data.id === BATTERY_REQUEST_TYPE.RENTAL) {
                    setValue("tripTransfer", "Swap");
                    setValue(
                      "tripTransferId",
                      BATTERY_REQUEST_TRANSFER_TYPE.SWAP,
                    );
                  }

                  clearErrors("requestType");
                }}
              />
            )}
          />

          <p
            className={`font-poppins text-xs font-light capitalize leading-4.5 ${
              requestTypeError ? "text-alert-red visible mt-2" : "invisible"
            }`}
          >
            {requestTypeError}
          </p>
        </div>

        {[BATTERY_REQUEST_TYPE.TRIP, BATTERY_REQUEST_TYPE.RENTAL].includes(
          watch("requestTypeId"),
        ) && (
          <div>
            <div className="mt-6">
              <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
                Transfer Type
              </p>

              <Controller
                name="tripTransfer"
                control={control}
                rules={rules}
                render={({ field }) => (
                  <PopoverDropdown
                    {...field}
                    formState={formState}
                    disabled={BATTERY_REQUEST_TYPE.RENTAL}
                    placeholder="Select transfer type"
                    options={batteryTransferForTripOptions}
                    onSelect={(data) => {
                      setValue("tripTransfer", data.value);
                      setValue("tripTransferId", data.id);
                      clearErrors("tripTransfer");
                    }}
                  />
                )}
              />

              <p
                className={`font-poppins text-xs font-light capitalize leading-4.5 ${
                  tripTransferError
                    ? "text-alert-red visible mt-2"
                    : "invisible"
                }`}
              >
                {tripTransferError}
              </p>
            </div>

            <div className="mt-6">
              <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
                {watch("requestTypeId") === BATTERY_REQUEST_TYPE.TRIP
                  ? "Trip ID"
                  : "Rental ID"}
              </p>

              <Controller
                name="tripId"
                control={control}
                rules={rules}
                render={({ field }) => (
                  <PopoverDropdown
                    {...field}
                    enableSearch
                    className="max-h-[200px] overflow-y-scroll"
                    placeholder={
                      watch("requestTypeId") === BATTERY_REQUEST_TYPE.TRIP
                        ? "Select Trip ID"
                        : "Select Rental ID"
                    }
                    searchPlaceholder={
                      watch("requestTypeId") === BATTERY_REQUEST_TYPE.TRIP
                        ? "Search Trip ID"
                        : "Search Rental ID"
                    }
                    formState={formState}
                    options={
                      watch("requestTypeId") === BATTERY_REQUEST_TYPE.TRIP
                        ? tripIds
                        : rentalIds
                    }
                    onSelect={(data) => {
                      setValue("tripId", data.id);
                      clearErrors("tripId");
                    }}
                  />
                )}
              />

              <p
                className={`font-poppins text-xs font-light capitalize leading-4.5 ${
                  tripIdError ? "text-alert-red visible mt-2" : "invisible"
                }`}
              >
                {tripIdError}
              </p>
            </div>
          </div>
        )}

        {/* should not appear for rentals request */}
        {watch("requestTypeId") !== BATTERY_REQUEST_TYPE.RENTAL &&
          watch("tripTransferId") === BATTERY_REQUEST_TRANSFER_TYPE.SWAP && (
            <div className="mt-6">
              <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
                Batteries to swap
              </p>

              <SelectSwapBatteries
                onSelect={onTripBatterySelect}
                hasError={selectedBatteryError}
                tripBatteryIds={tripBatteryIds}
                selectedBatteryIds={selectedBatteryIds}
              />

              <p
                className={`font-poppins text-xs font-light capitalize leading-4.5 ${
                  selectedBatteryError
                    ? "text-alert-red visible mt-2"
                    : "invisible"
                }`}
              >
                {selectedBatteryError}
              </p>
            </div>
          )}

        {/* should not appear for rentals request */}
        {watch("requestTypeId") !== BATTERY_REQUEST_TYPE.RENTAL && (
          <div className="mt-6">
            <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
              How many batteries do you require?
            </p>

            <CustomInputWithController
              intent="createUser"
              control={control}
              formState={formState}
              focus={inputFocus}
              setFocus={setInputFocus}
              name="batteryCount"
              placeholder="Enter Number"
              rules={{
                ...rules,
                validate: {
                  isNumber: (value) => {
                    return value > 0 || "Please enter valid number";
                  },
                },
              }}
            />
          </div>
        )}
      </div>
    </CustomLayout>
  );
};

export default RequestBatteries;
